/**
 * The icon set (web.md §7 "Icons"): Lucide 1.45, 1,834 glyphs, built from the prototype by
 * scripts/build-icons.mjs. The chrome subset is bundled eagerly; the full map (≈ 600 KB) is a lazy
 * asset fetched the first time a non-chrome icon or the picker is needed.
 *
 * Name resolution is the prototype's: a Lucide name, else one of its legacy aliases, else 'folder'.
 * Search is the prototype's: every word must appear in the name or its keywords or an alias; direct
 * name matches first, then alphabetical.
 */

import chromeData from './chrome-icons.json';
import lucideUrl from './lucide.json?url';

export type IconNode = readonly [string, Readonly<Record<string, string>>];
export interface IconData {
  nodes: readonly IconNode[];
  keywords: readonly string[];
}
interface IconFile {
  version: string;
  count?: number;
  aliases: Record<string, string>;
  icons: Record<string, IconData>;
}

const chrome = chromeData as unknown as IconFile;
const aliases: Readonly<Record<string, string>> = chrome.aliases;

/** Favourites first when browsing (the prototype's order). */
const FAVORITES = [
  'folder', 'inbox', 'star', 'tag', 'bookmark', 'heart', 'flame', 'zap', 'sparkles', 'brain', 'code', 'newspaper', 'rss', 'mail', 'globe',
  'briefcase-business', 'building-2', 'landmark', 'chart-no-axes-combined', 'circle-dollar-sign', 'science', 'flask-conical', 'leaf',
  'shield-check', 'graduation-cap', 'book-open', 'lightbulb', 'camera', 'music', 'headphones', 'gamepad-2', 'plane', 'car', 'tram-front',
  'map-pin', 'coffee',
];

let full: IconFile | null = null;
let names: string[] = [];
let searchIndex = new Map<string, string>();
let loading: Promise<void> | null = null;
const listeners = new Set<() => void>();
let version = 0;

function index(file: IconFile): void {
  full = file;
  names = Object.keys(file.icons).sort();
  searchIndex = new Map(
    names.map((name) => [
      name,
      `${name.replaceAll('-', ' ')} ${(file.icons[name]?.keywords ?? []).join(' ')} ${Object.keys(aliases).filter((a) => aliases[a] === name).join(' ')}`.toLowerCase(),
    ]),
  );
  version++;
  for (const listener of [...listeners]) listener();
}

/** Load the full set once. Resolves when every icon can be rendered and searched. */
export function loadAllIcons(): Promise<void> {
  if (full) return Promise.resolve();
  loading ??= fetch(lucideUrl)
    .then((r) => {
      if (!r.ok) throw new Error(`icons: ${r.status}`);
      return r.json() as Promise<IconFile>;
    })
    .then(index)
    .catch((error: unknown) => {
      loading = null;
      throw error;
    });
  return loading;
}

export function allIconsLoaded(): boolean {
  return full !== null;
}

/** Subscribe to "the full set arrived" (useSyncExternalStore). */
export function subscribeIcons(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function iconsVersion(): number {
  return version;
}

function has(name: string): boolean {
  return Boolean(full?.icons[name] ?? chrome.icons[name]);
}

/**
 * The Lucide name to draw for `name`, or null while the full set is still loading and `name` is not in
 * the chrome subset (the caller shows an empty box of the same size and calls `loadAllIcons`).
 */
export function resolveIcon(name: string | null | undefined): string | null {
  const n = String(name ?? '');
  if (has(n)) return n;
  const alias = aliases[n];
  if (alias !== undefined && has(alias)) return alias;
  if (!full) return null;
  return 'folder';
}

export function iconData(name: string): IconData | null {
  return full?.icons[name] ?? chrome.icons[name] ?? null;
}

/** "tram-front" → "Tram Front" (the prototype's label). */
export function iconLabel(name: string): string {
  const resolved = resolveIcon(name) ?? name;
  return resolved.split('-').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

/** Every icon name, sorted (empty until loaded). */
export function iconNames(): readonly string[] {
  return names;
}

/** The prototype's search. Empty query → favourites first, then everything. Needs the full set. */
export function searchIcons(query = ''): string[] {
  const words = String(query).toLowerCase().trim().replaceAll('-', ' ').split(/\s+/).filter(Boolean);
  if (!words.length) {
    const favorites = FAVORITES.filter((n) => searchIndex.has(n));
    const set = new Set(favorites);
    return [...favorites, ...names.filter((n) => !set.has(n))];
  }
  return names
    .filter((name) => words.every((word) => (searchIndex.get(name) ?? '').includes(word)))
    .sort((a, b) => {
      const directA = words.every((word) => a.replaceAll('-', ' ').includes(word));
      const directB = words.every((word) => b.replaceAll('-', ' ').includes(word));
      return Number(directB) - Number(directA) || a.localeCompare(b);
    });
}
